import type {
  ApiResponse,
  OrganizationBillingContext,
  OrganizationBillingTaxProfileResponseData,
  OrganizationBillingVatVerificationData,
} from '@fluenceur/types';
import { useQuery } from '@tanstack/react-query';
import { useRef } from 'react';
import { z } from 'zod';
import { useAuth } from '@/contexts/auth-context';
import {
  BillingTargetChangedError,
  useBillingActionScope,
} from '@/hooks/actions/use-billing-action-scope';
import api from '@/libs/axiosInstance';
import { readBillingContext } from '@/libs/billing-access';
import { readBillingTaxProfile } from './use-billing-tax-profile-query';

export const BILLING_VAT_VERIFICATION_QUERY_KEY = ['billingVatVerification'] as const;
const verificationSchema = z
  .object({
    organizationId: z.string().min(1),
    brandId: z.string().min(1),
    canCheck: z.boolean(),
    profileId: z.string().uuid().nullable(),
    status: z.enum([
      'not_required',
      'not_checked',
      'invalid_format',
      'valid',
      'invalid',
      'unavailable',
    ]),
    consultation: z
      .object({
        id: z.string().uuid(),
        status: z.enum(['valid', 'invalid', 'unavailable']),
        authority: z.enum(['vies', 'hmrc']),
        consultedAt: z.string().datetime({ offset: true }),
        checkedAt: z.string().datetime({ offset: true }).nullable(),
        reference: z.string().trim().min(1).max(512).nullable(),
      })
      .strict()
      .nullable(),
  })
  .strict()
  .refine((data) => {
    if (!data.profileId)
      return data.status === 'not_required' && !data.canCheck && !data.consultation;
    if (data.status === 'valid')
      return (
        data.consultation?.status === 'valid' &&
        !!data.consultation.checkedAt &&
        !!data.consultation.reference
      );
    if (data.status === 'invalid' || data.status === 'unavailable')
      return (
        data.consultation?.status === data.status &&
        data.consultation.checkedAt === null &&
        data.consultation.reference === null
      );
    return !data.consultation;
  });

export function readBillingVatVerification(
  value: unknown
): OrganizationBillingVatVerificationData | undefined {
  const result = verificationSchema.safeParse(value);
  return result.success ? result.data : undefined;
}

export function canAttemptBillingVatCheck(
  verification?: OrganizationBillingVatVerificationData
): verification is OrganizationBillingVatVerificationData {
  return (
    !!verification?.profileId &&
    verification.canCheck &&
    verification.status !== 'not_required' &&
    verification.status !== 'invalid_format'
  );
}

/** A new immutable profile is another proof target even under the same actor and tenant. */
export function useBillingVatProfileScope(profileId?: string | null) {
  const actor = useBillingActionScope();
  const currentProfile = useRef(profileId ?? null);
  currentProfile.current = profileId ?? null;
  const scope = { ...actor.scope, profileId: profileId ?? null };
  const assertCurrent = (target: typeof scope) => {
    actor.assertCurrent(target);
    if (target.profileId !== currentProfile.current)
      throw new BillingTargetChangedError('Billing profile changed');
  };
  return { scope, assertCurrent };
}

export const billingVatVerificationQueryKey = (scope: {
  userId: string | null;
  organizationId: string | null;
  brandId: string | null;
  profileId: string | null;
}) => [
  ...BILLING_VAT_VERIFICATION_QUERY_KEY,
  scope.userId,
  scope.organizationId,
  scope.brandId,
  scope.profileId,
];

export function useBillingVatVerificationQuery({
  context,
  profile,
  enabled = true,
}: {
  context: OrganizationBillingContext;
  profile?: OrganizationBillingTaxProfileResponseData;
  enabled?: boolean;
}) {
  const { isLoading } = useAuth();
  const saved = readBillingTaxProfile(profile);
  const { scope, assertCurrent } = useBillingVatProfileScope(saved?.profile?.id);
  const canRead =
    !!scope.userId &&
    !!scope.organizationId &&
    !!scope.brandId &&
    !!scope.profileId &&
    !!readBillingContext(context) &&
    context.channel === 'stripe' &&
    context.capabilities.canReadBilling &&
    context.organizationId === scope.organizationId &&
    context.brandId === scope.brandId &&
    saved?.organizationId === scope.organizationId &&
    saved.brandId === scope.brandId;
  return useQuery<OrganizationBillingVatVerificationData>({
    queryKey: billingVatVerificationQueryKey(scope),
    enabled: enabled && !isLoading && canRead,
    retry: false,
    refetchOnWindowFocus: false,
    queryFn: async ({ signal }) => {
      assertCurrent(scope);
      if (!canRead) throw new Error('No authorized VAT verification read');
      const response = await api.get<ApiResponse<unknown>>('/dashboard/billing/vat-verification', {
        signal,
      });
      assertCurrent(scope);
      const data = readBillingVatVerification(response.data.data);
      if (
        response.data.success !== true ||
        !data ||
        data.organizationId !== scope.organizationId ||
        data.brandId !== scope.brandId ||
        data.profileId !== scope.profileId ||
        (data.canCheck &&
          (!saved?.canManage ||
            !context.capabilities.canCommitPaidPlan ||
            context.cardSetup.exemption === 'impersonation'))
      ) {
        throw new Error('Invalid scoped VAT verification');
      }
      return data;
    },
  });
}
